import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { searchPhysicians, formatCurrency, checkExclusionsBatch, checkOwnershipBatch, checkResearchBatch, checkDualInterestBatch, checkPodBatch } from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";
import { useMyProviders } from "../context/MyProvidersContext";
import { useUser } from "@clerk/react";
import { getWatchedProvider, unwatchProvider, getWatchedProviders } from "../utils/alertsApi";
import WatchProviderButton from "../components/WatchProviderButton";
import CompareButton from "../components/CompareButton";
import { useCompare } from "../context/CompareContext";

const US_STATES = [
  "ALL","AK","AL","AR","AZ","CA","CO","CT","DC","DE","FL","GA","HI",
  "IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO",
  "MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR",
  "PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY",
];

const SPECIALTIES = [
  "ALL",
  "Allopathic & Osteopathic Physicians|Anesthesiology",
  "Allopathic & Osteopathic Physicians|Cardiology",
  "Allopathic & Osteopathic Physicians|Dermatology",
  "Allopathic & Osteopathic Physicians|Emergency Medicine",
  "Allopathic & Osteopathic Physicians|Family Medicine",
  "Allopathic & Osteopathic Physicians|Gastroenterology",
  "Allopathic & Osteopathic Physicians|General Surgery",
  "Allopathic & Osteopathic Physicians|Internal Medicine",
  "Allopathic & Osteopathic Physicians|Neurology",
  "Allopathic & Osteopathic Physicians|Obstetrics & Gynecology",
  "Allopathic & Osteopathic Physicians|Oncology",
  "Allopathic & Osteopathic Physicians|Ophthalmology",
  "Allopathic & Osteopathic Physicians|Orthopedic Surgery",
  "Allopathic & Osteopathic Physicians|Pathology",
  "Allopathic & Osteopathic Physicians|Pediatrics",
  "Allopathic & Osteopathic Physicians|Psychiatry & Neurology",
  "Allopathic & Osteopathic Physicians|Pulmonology",
  "Allopathic & Osteopathic Physicians|Radiology",
  "Allopathic & Osteopathic Physicians|Rheumatology",
  "Allopathic & Osteopathic Physicians|Urology",
];

function specialtyLabel(s) {
  if (s === "ALL") return "All specialties";
  return s.includes("|") ? s.split("|")[1] : s;
}

function exportResultsCsv(providers, dualInterestNpis = new Set(), tripleInterestNpis = new Set(), podNpis = new Map()) {
  const rows = [["First Name", "Last Name", "Specialty", "City", "State", "NPI",
    "Total Payments ($)", "Payment Count", "Company Count", "Year Count",
    "Dual Interest", "Triple Interest", "POD Risk (OIG Work Plan 2026)"]];
  for (const p of providers) {
    const isTriple = tripleInterestNpis.has(p.npi);
    const isDual = dualInterestNpis.has(p.npi) || isTriple;
    const podTier = podNpis.get(p.npi);
    rows.push([
      p.firstName || "", p.lastName || "", p.specialty || "",
      p.city || "", p.state || "", p.npi || "",
      p.totalPayments ? p.totalPayments.toFixed(2) : "0.00",
      p.paymentCount || 0,
      p.companyCount || 0,
      p.yearCount || 0,
      isDual ? "Yes" : "No",
      isTriple ? "Yes" : "No",
      podTier ? `Tier ${podTier}` : "No",
    ]);
  }
  const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `provider_search_results.csv`;
  a.click();
}

export default function ResultsPage({ year }) {
  const [searchParams] = useSearchParams();
  const firstName = searchParams.get("first") || "";
  const lastName = searchParams.get("last") || "";
  const [npi, setNpi] = useState(searchParams.get("npi") || "");
  const [state, setState] = useState(searchParams.get("state") || "ALL");
  const [specialty, setSpecialty] = useState(searchParams.get("specialty") || "ALL");
  const [excludedOnly, setExcludedOnly] = useState(searchParams.get("excludedOnly") === "true");

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [excludedNpis, setExcludedNpis] = useState(new Map());
  const [ownershipNpis, setOwnershipNpis] = useState(new Set());
  const [researchNpis, setResearchNpis] = useState(new Set());
  const [dualInterestNpis, setDualInterestNpis] = useState(new Set());
  const [tripleInterestNpis, setTripleInterestNpis] = useState(new Set());
  const [podNpis, setPodNpis] = useState(new Map());
  const [filtersOpen, setFiltersOpen] = useState(
    !!(searchParams.get("npi") || (searchParams.get("state") && searchParams.get("state") !== "ALL") || (searchParams.get("specialty") && searchParams.get("specialty") !== "ALL") || searchParams.get("excludedOnly") === "true")
  );

  const { isPro, isEnterprise, getResultLimit, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const isPaidTier = isPro;    
  const { user, isSignedIn } = useUser();
  const {
    filterActive, providerNpis, activeList, selectedListIdsString,
    selectedLists, totalSelectedProviders, syncing, syncError,
  } = useMyProviders();
  const [watchedNpis, setWatchedNpis] = useState(new Map());
  const [watchedProviders, setWatchedProviders] = useState([]);
  const [watchListOpen, setWatchListOpen] = useState(false);    

  const activeFilterCount = [
    npi.trim() !== "",
    state !== "ALL",
    specialty !== "ALL",
    excludedOnly,
  ].filter(Boolean).length;

  useEffect(() => {
    setLoading(true);
    setError(null);
    setExcludedNpis(new Map());

    const searchFn = filterActive && selectedListIdsString && user?.id
      ? fetch(`/api/bigquery?action=searchWithinMyProviders&userId=${encodeURIComponent(user.id)}&listIds=${encodeURIComponent(selectedListIdsString)}&firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}&npi=${encodeURIComponent(npi)}&state=${encodeURIComponent(state)}&specialty=${encodeURIComponent(specialty)}`)
          .then((r) => r.json())
          .then((data) => Array.isArray(data) ? data : (data.results || data.providers || []))
      : searchPhysicians(firstName, lastName, year, { npi, state, specialty, excludedOnly });

    searchFn
      .then((data) => {
        setResults(data);
        const npis = data.map((p) => p.npi).filter(Boolean);
        if (npis.length > 0) {
          checkExclusionsBatch(npis)
            .then((exclusions) => {
              const map = new Map(exclusions.map((e) => [e.npi, { oig: e.oig, sam: e.sam }]));
              setExcludedNpis(map);
            })
            .catch(() => {});
          checkOwnershipBatch(npis)
            .then((ownNpis) => setOwnershipNpis(new Set(ownNpis)))
            .catch(() => {});
          checkResearchBatch(npis)
            .then((resNpis) => setResearchNpis(new Set(resNpis)))
            .catch(() => {});
          checkDualInterestBatch(npis)
            .then((dualResults) => {
              setDualInterestNpis(new Set(dualResults.map((d) => d.npi)));
              setTripleInterestNpis(new Set(dualResults.filter((d) => d.hasTripleInterest).map((d) => d.npi)));
            })
            .catch(() => {});
          checkPodBatch(npis)
            .then((podList) => setPodNpis(new Map(podList.map((p) => [p.npi, p.podTier]))))
            .catch(() => {});
          // Check watched status for all results
          if (isSignedIn && user) {
            Promise.all(npis.map((npi) => getWatchedProvider(user.id, npi).then((data) => ({ npi, watched: !!data }))))
              .then((results) => {
                const map = new Map(results.map((r) => [r.npi, r.watched]));
                setWatchedNpis(map);
              })
              .catch(() => {});
          }
        }
      })      
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [firstName, lastName, year, npi, state, specialty, excludedOnly, filterActive, selectedListIdsString]);

// Load full watched providers list
  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedProviders(user.id)
      .then((data) => setWatchedProviders(data))
      .catch(() => {});
  }, [isSignedIn, user, watchedNpis]);

  // When filter active, results come directly from BigQuery (already filtered)
  // When filter inactive, use normal results
  const filteredResults = results;

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">

      {/* My Providers filter banner */}
      {filterActive && (
        <div className="mb-4 flex items-center justify-between px-3 py-2 rounded-lg bg-gold-500/10 border border-gold-500/30">
          <div className="flex items-center gap-2">
            <span className="text-gold-400 text-xs">◉</span>
            <span className="text-gold-400 text-xs font-medium">
              {selectedLists.length === 1
                ? selectedLists[0].list_name
                : `${selectedLists.length} lists`}
            </span>
            <span className="text-gold-400/50 text-xs">
              · {totalSelectedProviders.toLocaleString()} providers
            </span>
          </div>
          <Link to="/my-providers" className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors">
            Manage →
          </Link>
        </div>
      )}
      {syncing && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/50 text-xs animate-pulse">
          ⏳ Preparing your provider filter...
        </div>
      )}
      {syncError && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
          ✗ {syncError}
        </div>
      )}

      <div className="mb-6">
        <Link to="/" className="text-white/40 hover:text-white/70 text-sm transition-colors">
          ← New search
        </Link>
        <h1 className="font-display text-3xl text-white mt-3">
          Results for{" "}
          <span className="text-gold-400">
            {[firstName, lastName].filter(Boolean).join(" ") || "all providers"}
          </span>
        </h1>

      {!loading && !error && filteredResults.length > 0 && isPro && (
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => exportResultsCsv(
              isEnterprise ? filteredResults : filteredResults.slice(0, 500),
              dualInterestNpis, tripleInterestNpis, podNpis
            )}
            className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors"
          >
            ⬇ Export results {isEnterprise ? "" : "(up to 500)"}
          </button>
        </div>
      )}
      {!loading && !error && (
          <div className="mt-1 space-y-1">
            <p className="text-white/40 text-sm">
              {Math.min(filteredResults.length, getResultLimit())} of {filteredResults.length} provider{filteredResults.length !== 1 ? "s" : ""} shown · all years
              {!isPro && results.length > getResultLimit() && (
                <span className="text-gold-400 text-xs ml-2">
                  · Upgrade to see all results
                </span>
              )}
            </p>
            <p className="text-white/50 text-xs">
              ⚠ Exclusion flags shown for NPI-confirmed matches only — additional matches may appear on the provider detail page
            </p>
          </div>
        )}        
      </div>

      {/* Filter toggle */}
      <div className="mb-4">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
        >
          <span>⊞</span>
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-gold-500 text-navy-900 text-xs font-medium px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
          <span className="text-white/30">{filtersOpen ? "▲" : "▼"}</span>
        </button>

        {filtersOpen && (
          <div className="mt-3 bg-navy-800 border border-white/10 rounded-xl p-4 space-y-4">
            <div>
              <label className="text-white/40 text-xs mb-1.5 block">Search by NPI number instead</label>
              <input
                className="input-base text-sm py-2"
                placeholder="e.g. 1234567890"
                value={npi}
                maxLength={10}
                onChange={(e) => setNpi(e.target.value.replace(/\D/g, ""))}
              />
              {npi.trim() !== "" && (
                <p className="text-white/30 text-xs mt-1">Name filters are ignored when searching by NPI</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-xs mb-1.5 block">State</label>
                <select
                  className="w-full bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s === "ALL" ? "All states" : s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-white/40 text-xs mb-1.5 block">Specialty</label>
                <select
                  className="w-full bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                >
                  {SPECIALTIES.map((s) => (
                    <option key={s} value={s}>{specialtyLabel(s)}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Exclusion filter toggle - hidden for now */}
            {/* <div className="flex items-center justify-between bg-navy-700/50 border border-white/10 rounded-lg p-3">
              <div>
                <label className="text-white/70 text-sm font-medium block">
                  Excluded providers only
                </label>
                <p className="text-white/30 text-xs mt-0.5">
                  Show only providers on the OIG or SAM exclusion lists
                </p>
              </div>
              <button
                onClick={() => setExcludedOnly(!excludedOnly)}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                  excludedOnly ? "bg-gold-500" : "bg-white/10"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    excludedOnly ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div> */}            

            {activeFilterCount > 0 && (
              <button
                onClick={() => { setNpi(""); setState("ALL"); setSpecialty("ALL"); setExcludedOnly(false); }}
                className="text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                ✕ Clear all filters
              </button>
            )}            
          </div>
        )}
      </div>

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-1/2 mb-3" />
              <div className="h-3 bg-white/5 rounded w-3/4 mb-2" />
              <div className="h-3 bg-white/5 rounded w-1/4" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-sm">
          ⚠ {error} — the API may be temporarily unavailable.
        </div>
      )}

      {!loading && !error && results.length === 0 && !filterActive && (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3 text-white/20">◎</div>
          <p className="text-white/50 text-sm">No payment records found.</p>
          <p className="text-white/30 text-xs mt-1">Try adjusting your filters or a different spelling.</p>
        </div>
      )}

      {!loading && !error && results.length === 0 && filterActive && (
        <div className="card text-center py-12 border-gold-500/20 bg-gold-500/5">
          <div className="text-4xl mb-3">◉</div>
          <p className="text-gold-400 text-sm font-medium">
            No results in {selectedLists.length === 1 ? selectedLists[0]?.list_name : `${selectedLists.length} lists`}
          </p>
          <p className="text-white/40 text-xs mt-1">
            This provider was not found in your selected provider list{selectedLists.length !== 1 ? "s" : ""}.
          </p>
          <Link to="/my-providers" className="text-gold-400/60 hover:text-gold-400 text-xs mt-3 inline-block underline transition-colors">
            Manage My Providers →
          </Link>
        </div>
      )}

      <div className="space-y-3">
        {filteredResults.slice(0, getResultLimit()).map((physician) => (
          <div          
            key={physician.npi}
            className="card hover:border-gold-500/30 hover:bg-navy-700/50 transition-all group relative"
          >
            <Link
              to={`/physician/${physician.npi}?name=${encodeURIComponent(physician.firstName + " " + physician.lastName)}&state=${physician.state || ""}&latestYear=${physician.latestYear || 2025}&totalAllYears=${Math.round(physician.totalPayments)}`}
              className="block"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-white font-medium group-hover:text-gold-300 transition-colors">
                      {physician.firstName} {physician.lastName}
                    </h2>
                    {excludedNpis.get(physician.npi)?.oig && (
                      <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                        ⚠ OIG Excluded
                      </span>
                    )}
                    {excludedNpis.get(physician.npi)?.sam && (
                      <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs">
                        ⚠ SAM Excluded
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-sm mt-0.5">
                    {physician.specialty} · {physician.city}{physician.state ? `, ${physician.state}` : ""}
                  </p>
                  {physician.npi && (
                    <p className="text-white/20 text-xs mt-0.5 font-mono">NPI: {physician.npi}</p>
                  )}
                </div>
                <span className="text-white/30 group-hover:text-white/60 ml-4 mt-1">›</span>
              </div>
              <div className="flex gap-3 mt-3 flex-wrap items-center">
                <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-300">
                  {formatCurrency(physician.totalPayments)}
                </span>
                {physician.companyCount > 0 && (
                  <span className="badge bg-white/5 border border-white/10 text-white/50">
                    {physician.companyCount} {physician.companyCount === 1 ? "company" : "companies"}
                  </span>
                )}
                {physician.yearCount > 0 && (
                  <span className="badge bg-white/5 border border-white/10 text-white/50">
                    {physician.yearCount} {physician.yearCount === 1 ? "year" : "years"} of data
                    {!isPaidTier && physician.yearCount > 3 && (
                      <span className="ml-1 text-gold-400/70">· 🔒 2023–present only</span>
                    )}
                  </span>
                )}
              </div>
              </Link>
            <div className="mt-3 flex items-center justify-between gap-2">
              {/* ── Data flags ──────────────────────────────────────────── */}
              <div className="flex gap-2 flex-wrap">
                {physician.totalPayments > 0 && (
                  <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-400/80 text-xs">
                    💊 Payments
                  </span>
                )}
                {ownershipNpis.has(physician.npi) && (
                  <span className="badge bg-amber-500/10 border border-amber-500/20 text-amber-400/80 text-xs">
                    🏦 Ownership
                  </span>
                )}
                {researchNpis.has(physician.npi) && (
                  <span className="badge bg-teal-500/10 border border-teal-500/20 text-teal-400/80 text-xs">
                    🔬 Research
                  </span>
                )}
                {tripleInterestNpis.has(physician.npi) ? (
                  <span className="badge bg-red-500/10 border border-red-500/30 text-red-400/80 text-xs">
                    ⚠ Triple Interest
                  </span>
                ) : dualInterestNpis.has(physician.npi) ? (
                  <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400/80 text-xs">
                    ⚑ Dual Interest
                  </span>
                ) : null}
                {podNpis.has(physician.npi) && (
                  <span className="badge bg-blue-500/10 border border-blue-500/30 text-blue-400/80 text-xs">
                    ⚠ POD Risk Tier {podNpis.get(physician.npi)}
                  </span>
                )}
                {(excludedNpis.get(physician.npi)?.oig || excludedNpis.get(physician.npi)?.sam) && (
                  <span className="badge bg-red-500/10 border border-red-500/20 text-red-400/80 text-xs">
                    ⚠ Excluded
                  </span>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
              <WatchProviderButton
                npi={physician.npi}
                providerName={physician.firstName + " " + physician.lastName}
                initialWatched={watchedNpis.get(physician.npi) || false}
                onWatchChange={(npi, watched) => {
                  setWatchedNpis((prev) => new Map(prev).set(npi, watched));
                  if (watched) {
                    setWatchedProviders((prev) => [...prev, { npi, provider_name: physician.firstName + " " + physician.lastName }]);
                  } else {
                    setWatchedProviders((prev) => prev.filter((p) => p.npi !== npi));
                  }
                }}
              />
              <CompareButton provider={physician} />
              </div>
            </div>
          </div>         
        ))}
      </div>

{/* Watching List */}
      {isSignedIn && isPro && (
        <div className="card mt-6">
          <button
            onClick={() => setWatchListOpen(!watchListOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm">🔔 Provider Watch List</span>
              {watchedProviders.length > 0 && (
                <span className="bg-gold-500/20 border border-gold-500/30 text-gold-400 text-xs px-2 py-0.5 rounded-full">
                  {watchedProviders.length}
                </span>
              )}
            </div>
            <span className="text-white/30 text-xs">{watchListOpen ? "▲" : "▼"}</span>
          </button>

          {watchListOpen && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
              {watchedProviders.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">
                  You aren't watching any providers yet. Click "🔕 Watch" on any provider to add them.
                </p>
              ) : (
                watchedProviders.map((p) => (
                  <div key={p.npi} className="flex items-center justify-between bg-navy-700/50 border border-white/5 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="min-w-0">
                        <Link
                          to={`/physician/${p.npi}?name=${encodeURIComponent(p.provider_name)}`}
                          className="text-white/80 text-sm hover:text-gold-300 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {p.provider_name}
                        </Link>
                        <p className="text-white/30 text-xs font-mono mt-0.5">NPI: {p.npi}</p>
                      </div>
                      <CompareButton provider={{
                        npi: p.npi,
                        firstName: (p.provider_name || "").split(" ")[0] || "",
                        lastName: (p.provider_name || "").split(" ").slice(1).join(" ") || "",
                        state: p.state || "",
                        totalPayments: p.total_payments || 0,
                      }} />
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <div className="flex gap-1">
                        {p.alert_payments && (
                          <span className="text-white/30 text-xs">💊 Payments</span>
                        )}
                        {p.alert_exclusions && (
                          <span className="text-white/30 text-xs ml-2">⚠ Exclusions</span>
                        )}
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          await unwatchProvider(user.id, p.npi);
                          setWatchedProviders((prev) => prev.filter((w) => w.npi !== p.npi));
                          setWatchedNpis((prev) => new Map(prev).set(p.npi, false));
                        }}
                        className="text-white/20 hover:text-red-400 text-xs transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}      
    </main>
  );
}